import { memo, useMemo, useRef } from "react";
import { StyledGoogleMaps } from "../../styled/googleMaps";
import { ARGENTINA_CENTER } from "../../ui/art/traslados/pendientes/AgenciasAsignacionMap/mapConstants";
import GooglePlacesAutocomplete from "../formComponents/GooglePlacesAutocomplete";

const isValidLocation = (location) => {
  const lat = Number(location?.lat);
  const lng = Number(location?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};

const toCoord = (location) => `${Number(location.lat)},${Number(location.lng)}`;

const formatDirectionsPoint = (search) => {
  if (isValidLocation(search?.location)) {
    return encodeURIComponent(toCoord(search.location));
  }
  const query = String(search?.query || "").trim();
  if (query) {
    return encodeURIComponent(query);
  }
  return null;
};

const buildEmbedUrl = (searches = []) => {
  const points = searches.map(formatDirectionsPoint).filter(Boolean);

  if (points.length === 0) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(toCoord(ARGENTINA_CENTER))}&z=4&output=embed`;
  }

  if (points.length === 1) {
    return `https://maps.google.com/maps?q=${points[0]}&output=embed`;
  }

  const saddr = points[0];
  const daddr = points.slice(1).join("+to:");
  return `https://maps.google.com/maps?saddr=${saddr}&daddr=${daddr}&output=embed&travelmode=driving`;
};

const GooglePlacesMap = ({
  searches = [],
  disabled,
  idArt,
  showSearch = true,
  showMap = true,
}) => {
  const searchesRef = useRef(searches);
  searchesRef.current = searches;
  const mapKey = searches.map(formatDirectionsPoint).filter(Boolean).join("|");
  const mapSrc = useMemo(
    () => buildEmbedUrl(searches),
    // mapKey evita recalcular el iframe en cada render del form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapKey],
  );
  const embedTitle = searches[0]?.title || "Mapa";

  const handlePlaceSelected =
    (search) =>
      (address) => {
        searchesRef.current
          .find((item) => item.key === search.key)
          ?.onPlaceSelected(address);
      };

  return (
    <>
      {showSearch &&
        searches.map((search) => (
          <StyledGoogleMaps.SearchControl key={search.key}>
            <GooglePlacesAutocomplete
              autoComplete="off"
              disabled={disabled}
              fullWidth
              handleSubmit={handlePlaceSelected(search)}
              idArt={idArt}
              label={search.label}
              type="search"
              variant="outlined"
            />
          </StyledGoogleMaps.SearchControl>
        ))}
      {showMap && (
        <StyledGoogleMaps.MapContainer>
          <StyledGoogleMaps.EmbedFrame
            key={mapSrc}
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={mapSrc}
            title={embedTitle}
          />
        </StyledGoogleMaps.MapContainer>
      )}
    </>
  );
};

export default memo(GooglePlacesMap, (prev, next) => {
  if (prev.disabled !== next.disabled) return false;
  if (prev.idArt !== next.idArt) return false;
  if (prev.showSearch !== next.showSearch) return false;
  if (Boolean(prev.showMap) !== Boolean(next.showMap)) return false;
  if (prev.searches.length !== next.searches.length) return false;
  return prev.searches.every((search, index) => {
    const other = next.searches[index];
    return (
      search.key === other.key &&
      search.label === other.label &&
      search.query === other.query &&
      Number(search.location?.lat) === Number(other.location?.lat) &&
      Number(search.location?.lng) === Number(other.location?.lng)
    );
  });
});
